import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  CheckCircle,
  XCircle,
  RotateCcw,
  Save
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import timesheetApprovalService from '../../services/timesheetApprovalService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const TimesheetApprovalForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isViewMode = location.pathname.includes('/view')
  const isApproveMode = location.pathname.includes('/approve')
  const approvalData = location.state?.approvalData

  const [approval, setApproval] = useState(null)
  const [itemApprovals, setItemApprovals] = useState([])
  const [approvalNotes, setApprovalNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true)

  useEffect(() => {
    if (approvalData?._id) {
      loadApprovalData(approvalData._id)
    }
  }, [approvalData])

  const loadApprovalData = async (id) => {
    try {
      setIsLoadingData(true)
      const response = await timesheetApprovalService.getTimesheetApprovalById(id)
      if (response.success && response.data) {
        setApproval(response.data)
        setItemApprovals(response.data.itemApprovals || [])
        setApprovalNotes(response.data.approvalNotes || '')
        setRejectionReason(response.data.rejectionReason || '')
      }
    } catch (error) {
      console.error('Error loading approval data:', error)
      toast.error('Failed to load approval data')
    } finally {
      setIsLoadingData(false)
    }
  }

  const handleItemApprovalChange = (index, field, value) => {
    const updated = [...itemApprovals]
    updated[index] = {
      ...updated[index],
      [field]: value
    }
    setItemApprovals(updated)
  }

  const formatHours = (totalMinutes) => {
    if (!totalMinutes) return '0h 0m'
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${hours}h ${minutes}m`
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatWeekRange = (weekStartDate, weekEndDate) => {
    if (!weekStartDate || !weekEndDate) return '-'
    const start = new Date(weekStartDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    const end = new Date(weekEndDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    return `${start} - ${end}`
  }

  const handleApprove = async () => {
    if (!approval) return

    try {
      setIsLoading(true)
      const response = await timesheetApprovalService.approveTimesheet(
        approval._id,
        approvalNotes,
        itemApprovals
      )
      if (response.success) {
        toast.success('Timesheet approved successfully!')
        navigate('/timesheets/timesheet-approval')
      } else {
        toast.error(response.message || 'Failed to approve timesheet')
      }
    } catch (error) {
      console.error('Error approving timesheet:', error)
      if (!error._errorHandled) {
        toast.error('Failed to approve timesheet')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReject = async () => {
    if (!approval) return
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason')
      return
    }

    try {
      setIsLoading(true)
      const response = await timesheetApprovalService.rejectTimesheet(
        approval._id,
        rejectionReason,
        itemApprovals
      )
      if (response.success) {
        toast.success('Timesheet rejected successfully!')
        navigate('/timesheets/timesheet-approval')
      } else {
        toast.error(response.message || 'Failed to reject timesheet')
      }
    } catch (error) {
      console.error('Error rejecting timesheet:', error)
      if (!error._errorHandled) {
        toast.error('Failed to reject timesheet')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReturn = async () => {
    if (!approval) return

    try {
      setIsLoading(true)
      const response = await timesheetApprovalService.returnTimesheet(
        approval._id,
        approvalNotes
      )
      if (response.success) {
        toast.success('Timesheet returned for revision successfully!')
        navigate('/timesheets/timesheet-approval')
      } else {
        toast.error(response.message || 'Failed to return timesheet')
      }
    } catch (error) {
      console.error('Error returning timesheet:', error)
      if (!error._errorHandled) {
        toast.error('Failed to return timesheet')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Timesheets', path: '/timesheets' },
    { label: 'Approvals', path: '/timesheets/timesheet-approval' },
    { 
      label: isViewMode ? 'View Approval' : 'Approve Timesheet', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoadingData) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading approval details...</p>
        </div>
      </div>
    )
  }

  if (!approval) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#dc3545' }}>Approval not found</p>
          <button
            onClick={() => navigate('/timesheets/timesheet-approval')}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              background: '#292752',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Back to Approvals
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isViewMode ? 'View Timesheet Approval' : 'Approve Timesheet'}</h1>
          <p>{isViewMode ? 'View timesheet approval details' : 'Review and approve or reject timesheet'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/timesheets/timesheet-approval')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        {/* Timesheet Information */}
        <div className="sac-form-header">
          <h2>Timesheet Information</h2>
        </div>
          
        <div className="form-content">
          <div className="section-header"><h3>Timesheet Summary</h3></div>
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Approval Number</label>
              <input
                type="text"
                value={approval.approvalNumber || '-'}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Timesheet Number</label>
              <input
                type="text"
                value={approval.timesheetNumber || '-'}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Employee</label>
              <input
                type="text"
                value={`${approval.employeeName || ''} (${approval.employeeCode || ''})`}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Department</label>
              <input
                type="text"
                value={approval.department || '-'}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Week</label>
              <input
                type="text"
                value={formatWeekRange(approval.weekStartDate, approval.weekEndDate)}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Total Hours</label>
              <input
                type="text"
                value={`${approval.totalHours || 0}h`}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Billable Hours</label>
              <input
                type="text"
                value={`${approval.billableHours || 0}h`}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Overtime Hours</label>
              <input
                type="text"
                value={`${approval.overtimeHours || 0}h`}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Billing Amount</label>
              <input
                type="text"
                value={formatCurrency(approval.totalBillingAmount, approval.currency)}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <input
                type="text"
                value={approval.status || '-'}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <input
                type="text"
                value={approval.priority || '-'}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Submitted Date</label>
              <input
                type="text"
                value={approval.submittedDate ? new Date(approval.submittedDate).toLocaleDateString() : '-'}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
          </div>
        </div>

        <div className="form-content">
          <div className="section-header"><h3>Time Entries</h3></div>

        <div style={{ 
          border: '1px solid #e9ecef', 
          borderRadius: '6px', 
          overflow: 'hidden',
          background: '#fff',
          marginBottom: '24px'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Entry #</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Project</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Activity</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Hours</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Billable</th>
                {!isViewMode && (
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Action</th>
                )}
                {isViewMode && (
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Status</th>
                )}
              </tr>
            </thead>
            <tbody>
              {itemApprovals.map((item, index) => {
                const timeEntry = item.timeEntry && typeof item.timeEntry === 'object' && item.timeEntry._id ? item.timeEntry : null
                const hours = item.hours || (timeEntry?.totalMinutes ? timeEntry.totalMinutes / 60 : 0)
                return (
                  <tr key={item.timeEntry?._id || item.timeEntry || index} style={{ borderBottom: '1px solid #e9ecef' }}>
                    <td style={{ padding: '12px' }}>{item.entryNumber || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      {item.projectName || item.project?.projectName || '-'}
                      {item.projectCode && (
                        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{item.projectCode}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {timeEntry?.activityType || '-'}
                      {timeEntry?.activity && (
                        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{timeEntry.activity}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {formatHours(hours * 60)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {item.isBillable ? (
                        <span style={{ color: '#28a745', fontWeight: '600' }}>Yes</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-secondary, #5B6478)' }}>No</span>
                      )}
                    </td>
                    {!isViewMode && (
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <select
                          value={item.status || 'Pending'}
                          onChange={(e) => handleItemApprovalChange(index, 'status', e.target.value)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #e9ecef',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontFamily: 'Inter, sans-serif'
                          }}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </td>
                    )}
                    {isViewMode && (
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span className={`status-badge ${
                          item.status === 'Approved' ? 'active' : 
                          item.status === 'Rejected' ? 'danger' : 'pending'
                        }`}>
                          {item.status || 'Pending'}
                        </span>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Approval Notes / Rejection Reason */}
        {!isViewMode && (
          <>
            <div className="form-group">
              <label htmlFor="approvalNotes" className="form-label">
                Approval Notes
              </label>
              <textarea
                id="approvalNotes"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                className="form-input"
                rows="3"
                placeholder="Add notes about this approval..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="rejectionReason" className="form-label">
                Rejection Reason (if rejecting)
              </label>
              <textarea
                id="rejectionReason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="form-input"
                rows="3"
                placeholder="Provide reason for rejection..."
              />
            </div>
          </>
        )}

        {isViewMode && (
          <>
            {approval.approvalNotes && (
              <div className="form-group">
                <label className="form-label">Approval Notes</label>
                <textarea
                  value={approval.approvalNotes}
                  readOnly
                  className="form-input"
                  rows="3"
                  style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                />
              </div>
            )}
            {approval.rejectionReason && (
              <div className="form-group">
                <label className="form-label">Rejection Reason</label>
                <textarea
                  value={approval.rejectionReason}
                  readOnly
                  className="form-input"
                  rows="3"
                  style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                />
              </div>
            )}
          </>
        )}

        </div>

        {/* Action Buttons */}
        {!isViewMode && approval.status === 'Pending' && (
          <div className="form-actions">
            <button
              type="button"
              onClick={handleApprove}
              disabled={isLoading}
              className="btn btn-success"
              style={{
                padding: '10px 24px',
                background: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              <CheckCircle size={16} />
              {isLoading ? 'Approving...' : 'Approve'}
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={isLoading || !rejectionReason.trim()}
              className="btn btn-danger"
              style={{
                padding: '10px 24px',
                background: '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: isLoading || !rejectionReason.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading || !rejectionReason.trim() ? 0.6 : 1
              }}
            >
              <XCircle size={16} />
              {isLoading ? 'Rejecting...' : 'Reject'}
            </button>
            <button
              type="button"
              onClick={handleReturn}
              disabled={isLoading}
              className="btn btn-secondary"
              style={{
                padding: '10px 24px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                background: '#fff',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                color: '#495057'
              }}
            >
              <RotateCcw size={16} />
              {isLoading ? 'Returning...' : 'Return for Revision'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default TimesheetApprovalForm

